/**
 * Модальное окно для настройки массового размещения грядок
 * Открывается в режиме "Посадка" и позволяет выбрать культуру и размер сетки грядок
 */

/** @jsxImportSource @emotion/react */
import React from 'react';
import { css } from '@emotion/react';
import { Modal, Form, InputNumber, Select, Space, Typography, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { useGetSaveCropsQuery } from '../../api/cropsApi';
import commonStyles from '../../styles/commonStyles';

const { Text } = Typography;

const styles = {
  preview: css({
    padding: 12,
    background: '#f5f5f5',
    borderRadius: 4,
    marginTop: 16,
  }),
};

interface BulkBedPlacementModalProps {
  saveId: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (params: { cropId: string; gridRows: number; gridCols: number }) => void; // CN-0012: UUID-only for cropId
  maxBoundingBox?: number;
}

const BulkBedPlacementModal: React.FC<BulkBedPlacementModalProps> = ({
  saveId,
  open,
  onClose,
  onConfirm,
  maxBoundingBox = 32,
}) => {
  const { t } = useTranslation('planning');
  const [form] = Form.useForm();

  // Загрузка библиотеки культур текущего сейва.
  const { data: crops = [], isLoading: cropsLoading } = useGetSaveCropsQuery(
    { saveId, limit: 500, offset: 0 },
    { skip: !open || !saveId }
  );

  // Вычисляемые значения для preview
  const cropId = Form.useWatch('cropId', form);
  const gridRows = Form.useWatch('gridRows', form) || 0;
  const gridCols = Form.useWatch('gridCols', form) || 0;

  const selectedCrop = crops.find((c: any) => c.id === cropId);
  const totalBeds = gridRows * gridCols;

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onConfirm({
        cropId: values.cropId,
        gridRows: values.gridRows,
        gridCols: values.gridCols,
      });
      form.resetFields();
    } catch (error) {
      // Валидация не прошла
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t('bulkBedModal.title')}
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText={t('bulkBedModal.place')}
      cancelText={t('common:actions.cancel')}
      width={500}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          gridRows: 3,
          gridCols: 6,
        }}
      >
        {/* Культура */}
        <Form.Item
          name="cropId"
          label={t('bulkBedModal.crop')}
          rules={[{ required: true, message: t('bulkBedModal.selectCrop') }]}
        >
          <Select
            showSearch
            placeholder={t('bulkBedModal.selectCrop')}
            loading={cropsLoading}
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={crops.map((crop: any) => ({
              value: crop.id,
              label: `${crop.name}${crop.latinName ? ` (${crop.latinName})` : ''}`,
            }))}
          />
        </Form.Item>

        {/* Размер сетки */}
        <Space css={commonStyles.fullWidth} size="middle">
          <Form.Item
            name="gridRows"
            label={t('bulkBedModal.width')}
            rules={[
              { required: true, message: t('bulkBedModal.widthRequired') },
              { type: 'number', min: 1, max: maxBoundingBox, message: t('bulkBedModal.range', { max: maxBoundingBox }) },
            ]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              placeholder="3"
              min={1}
              max={maxBoundingBox}
              style={{ width: 120 }}
            />
          </Form.Item>

          <Form.Item
            name="gridCols"
            label={t('bulkBedModal.length')}
            rules={[
              { required: true, message: t('bulkBedModal.lengthRequired') },
              { type: 'number', min: 1, max: maxBoundingBox, message: t('bulkBedModal.range', { max: maxBoundingBox }) },
            ]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              placeholder="6"
              min={1}
              max={maxBoundingBox}
              style={{ width: 120 }}
            />
          </Form.Item>
        </Space>

        {/* Предпросмотр */}
        {cropId && gridRows > 0 && gridCols > 0 && (
          <div css={styles.preview}>
            <Space direction="vertical" css={commonStyles.fullWidth}>
              <Text strong>{t('bulkBedModal.preview.title')}</Text>
              <Text>{t('bulkBedModal.preview.crop', { name: selectedCrop?.name })}</Text>
              <Text>
                {t('bulkBedModal.preview.gridSize', { rows: gridRows, cols: gridCols })}
              </Text>
              <Text type="success">
                {t('bulkBedModal.preview.willCreate', { count: totalBeds })}
              </Text>
            </Space>
          </div>
        )}

        {totalBeds > 100 && (
          <Alert
            message={t('bulkBedModal.manyBeds.title')}
            description={t('bulkBedModal.manyBeds.description', { count: totalBeds })}
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Form>

      <Alert
        message={t('bulkBedModal.nextStep.title')}
        description={t('bulkBedModal.nextStep.description')}
        type="info"
        showIcon
        style={{ marginTop: 16 }}
      />
    </Modal>
  );
};

export default BulkBedPlacementModal;
